"use client";

import { useCallback, useSyncExternalStore } from "react";

/**
 * External store tracking which canvas nodes are currently being processed
 * by the auto-run scheduler.
 *
 * We avoid stashing this on the React Flow node data (so it doesn't pollute
 * the saved canvas / undo history) and we avoid a Context (which would cause
 * every node to re-render whenever any node enters/exits the active set).
 *
 * Each node component subscribes via `useIsAutoRunActive(id)` and only
 * re-renders when its own id's membership in the active set changes.
 */

const activeIds = new Set<string>();
const idListeners = new Map<string, Set<() => void>>();

const notifyId = (id: string) => {
  const set = idListeners.get(id);
  if (!set) return;
  for (const fn of set) {
    try {
      fn();
    } catch {}
  }
};

export function markAutoRunActive(id: string): void {
  if (activeIds.has(id)) return;
  activeIds.add(id);
  notifyId(id);
}

export function markAutoRunInactive(id: string): void {
  if (!activeIds.has(id)) return;
  activeIds.delete(id);
  notifyId(id);
}

/** Replace the entire active set in one shot — used by the scheduler on each
 *  poll so we don't have to compute diffs at the call site. Notifies only the
 *  ids whose membership actually changed. */
export function setAutoRunActiveSet(nextIds: Iterable<string>): void {
  const next = new Set<string>(nextIds);
  const changed: string[] = [];
  for (const id of activeIds) {
    if (!next.has(id)) changed.push(id);
  }
  for (const id of next) {
    if (!activeIds.has(id)) changed.push(id);
  }
  if (changed.length === 0) return;
  activeIds.clear();
  for (const id of next) activeIds.add(id);
  for (const id of changed) notifyId(id);
}

export function clearAutoRunActive(): void {
  if (activeIds.size === 0) return;
  const snapshot = Array.from(activeIds);
  activeIds.clear();
  for (const id of snapshot) notifyId(id);
}

function subscribe(id: string, cb: () => void): () => void {
  let set = idListeners.get(id);
  if (!set) {
    set = new Set<() => void>();
    idListeners.set(id, set);
  }
  set.add(cb);
  return () => {
    const s = idListeners.get(id);
    if (!s) return;
    s.delete(cb);
    if (s.size === 0) idListeners.delete(id);
  };
}

// Server snapshot is a stable empty value. Defined at module scope so React's
// useSyncExternalStore can identity-compare it across renders.
const getServerSnapshot = () => false;

/**
 * React hook — returns `true` while `id` is currently being processed by the
 * auto-run scheduler. Subscribed per id, so an unrelated node entering the
 * active set does NOT re-render this node.
 *
 * The `subscribe` / `getSnapshot` callbacks are memoized by `id` so React's
 * `useSyncExternalStore` doesn't tear down and rebuild the subscription on
 * every render.
 */
export function useIsAutoRunActive(id: string): boolean {
  const subscribeForId = useCallback(
    (cb: () => void) => subscribe(id, cb),
    [id],
  );
  const getSnapshot = useCallback(() => activeIds.has(id), [id]);
  return useSyncExternalStore(subscribeForId, getSnapshot, getServerSnapshot);
}
